import { useEffect, useState } from "react";
import { Form, Tabs, Tab } from "react-bootstrap";
import Editor from "@monaco-editor/react";
import styles from "../../Assets/css/codingform.module.css";

interface ICCStep2Props {
	data: ICodingData;
	next: (val: ICodingData) => unknown;
	prev: (data: ICodingData) => void;
}

export default function CCStep2(props: ICCStep2Props) {
	const { next, prev, data } = props;

	const [questionCode, setQuestionCode] = useState<{
		c: string;
		"c++": string;
		java: string;
		python3: string;
	}>({
		c: "",
		"c++": "",
		java: "",
		python3: "",
	});
	const [solutionCode, setSolutionCode] = useState<{
		C: Array<string>;
		"C++": Array<string>;
		Java: Array<string>;
		Python3: Array<string>;
	}>({
		C: [""],
		"C++": [""],
		Java: [""],
		Python3: [""],
	});

	useEffect(() => {
		let temp_obj = {
			c: "",
			"c++": "",
			java: "",
			python3: "",
		};

		data.questionCode.forEach((val) => {
			temp_obj[val.language] = val.code;
		});

		setQuestionCode(temp_obj);

		// sets state for solutions
		let temp_code = {
			C: [""],
			"C++": [""],
			Java: [""],
			Python3: [""],
		};
		data.solutions.forEach((val) => {
			temp_code[val.language] = val.codeSolutions;
		});
		setSolutionCode(temp_code);
	}, [data]);

	function getEditorLang(lang: string): "c_cpp" | "c" | "java" | "python" {
		switch (lang) {
			case "C++":
				return "c_cpp";

			case "C":
				return "c";

			case "Java":
				return "java";

			case "Python3":
				return "python";
		}

		return "c_cpp";
	}

	function getLang(lang: language_options): "c" | "c++" | "java" | "python3" {
		switch (lang) {
			case "C++":
				return "c++";

			case "C":
				return "c";

			case "Java":
				return "java";

			case "Python3":
				return "python3";

			default:
				return "c++";
		}
	}

	function setCodeData(val: string, lang: "c" | "c++" | "java" | "python3") {
		setQuestionCode((prev) => {
			prev[lang] = val;
			return { ...prev };
		});
	}
	function setSolutionData(
		val: string,
		lang: "C" | "C++" | "Java" | "Python3",
		index: number
	) {
		setSolutionCode((prev) => {
			prev[lang][index] = val;
			return { ...prev };
		});
	}

	function handleChange(funcType: "next" | "prev") {
		let sel_language = Object.keys(questionCode);
		let sel_language_sol = Object.keys(solutionCode);
		let boilerPlateCode: Array<{
			language: "c" | "c++" | "java" | "python3";
			code: string;
		}> = [];

		let solutionPlateCode: Array<{
			language: language_options;
			codeSolutions: Array<string>;
		}> = [];

		for (let i = 0; i < sel_language.length; i++) {
			let lang: string = sel_language[i];

			if (
				lang === "c" ||
				lang === "c++" ||
				lang === "python3" ||
				lang === "java"
			) {
				let temp_obj: {
					language: "c" | "c++" | "java" | "python3";
					code: string;
				} = {
					language: "c++",
					code: "",
				};

				let temp_code = questionCode[lang];
				temp_obj.code = temp_code;

				temp_obj.language = lang;

				boilerPlateCode.push(temp_obj);
			}
		}

		// changes
		for (let i = 0; i < sel_language_sol.length; i++) {
			let lang: string = sel_language_sol[i];

			if (
				lang === "C" ||
				lang === "C++" ||
				lang === "Python3" ||
				lang === "Java"
			) {
				let temp_obj: {
					language: "C" | "C++" | "Java" | "Python3";
					codeSolutions: Array<string>;
				} = {
					language: "C",
					codeSolutions: [],
				};

				let temp_code = solutionCode[lang];
				temp_obj.language = lang;
				temp_obj.codeSolutions = temp_code;

				solutionPlateCode.push(temp_obj);
			}
		}
		if (funcType === "next") {
			next({
				...data,
				questionCode: boilerPlateCode,
				solutions: solutionPlateCode,
			});
		} else {
			prev({
				...data,
				questionCode: boilerPlateCode,
				solutions: solutionPlateCode,
			});
		}
	}

	return (
		<div className="d-flex justify-content-center align-items-center my-4">
			<Form className={`${styles.step_one_form}`}>
				<Form.Group>
					<Form.Label
						style={{
							fontWeight: 600,
							fontSize: "24px",
						}}
					>
						Boilerplate Codes: (Optional)
					</Form.Label>

					<Tabs
						defaultActiveKey={data.languages[0]}
						id="testcase-tab"
						className="mb-3"
					>
						{data.languages.map((lang: language_options, index) => {
							return (
								<Tab eventKey={lang} title={lang} key={index}>
									<section
										key={index}
										style={{
											border: "1px solid black",
											padding: "10px",
											marginBottom: "20px",
										}}
									>
										<p>Code for {lang}</p>
										<Editor
											defaultLanguage="c"
											defaultValue={
												questionCode[getLang(lang)]
											}
											className={styles.editor}
											language={getEditorLang(lang)}
											theme="vs-dark"
											onChange={(e) => {
												if (!e) return;
												let temp_lang:
													| "c"
													| "c++"
													| "java"
													| "python3"
													| undefined;

												switch (lang) {
													case "C++":
														temp_lang = "c++";
														break;

													case "Python3":
														temp_lang = "python3";
														break;

													case "C":
														temp_lang = "c";
														break;

													case "Java":
														temp_lang = "java";
														break;

													default:
														return;
												}

												if (!temp_lang) return;
												setCodeData(e, temp_lang);
											}}
										/>
										<br />
										<p>Solution for {lang}</p>

										{solutionCode[lang].map(
											(val: string, index: number) => {
												return (
													<div
														className="EachSolution mb-2"
														key={index}
													>
														<Editor
															defaultLanguage="c"
															defaultValue={val}
															className={
																styles.editor
															}
															language={getEditorLang(
																lang
															)}
															theme="vs-dark"
															onChange={(e) => {
																if (!e) return;
																let temp_lang:
																	| "C"
																	| "C++"
																	| "Java"
																	| "Python3"
																	| undefined;

																temp_lang =
																	lang;

																if (!temp_lang)
																	return;
																setSolutionData(
																	e,
																	temp_lang,
																	index
																);
															}}
														/>
													</div>
												);
											}
										)}
									</section>
								</Tab>
							);
						})}
					</Tabs>
				</Form.Group>

				<div className="my-3">
					<button
						className="my-2 px-3 py-1 rounded hover:bg-green-700"
						style={{
							backgroundColor: "blue",
							color: "#fff",
						}}
						onClick={() => handleChange("prev")}
					>
						Prev
					</button>
					<button
						className="my-2 mx-2 px-3 py-1 rounded hover:bg-green-700"
						style={{
							backgroundColor: "blue",
							color: "#fff",
						}}
						onClick={() => handleChange("next")}
					>
						Next
					</button>
				</div>
			</Form>
		</div>
	);
}
